import React, { ReactNode, useEffect } from "react";
import styled from "styled-components";

import { IThemeProps } from "@/styles/Theme";
import { moveDownAndFadeIn, moveUpAndFadeOut } from "@/styles/utils/Mixins";
import Svg from "@/styles/utils/Svg";

export const animationHeight = "100px";

export type OverlayProps = {
  icons: IIcons;
  dismiss?: boolean;
  onClose?: () => void;
  showCloseButton?: boolean;
  children?: ReactNode;
};

export const Overlay: React.FC<OverlayProps> = ({
  icons,
  dismiss,
  onClose,
  showCloseButton,
  children,
}) => {
  useEffect(() => {
    if (onClose && dismiss) {
      setTimeout(onClose, 150);
    }
  }, [dismiss]);

  const className = dismiss ? "fadeOut" : "fadeIn";

  return (
    <Flexbox className={showCloseButton ? "" : className}>
      <Content>
        {children}
        {showCloseButton && (
          <CloseButton onClick={onClose} data-qa="overlay:btn:close">
            <Svg contents={icons.CloseLite} height={10} width={10} />
          </CloseButton>
        )}
      </Content>
    </Flexbox>
  );
};

const CloseButton = styled.div<IThemeProps>`
  color: ${(props) => props.theme.colors.fontTextLight};
  position: absolute;
  top: 0;
  right: 0;
  padding: 10px;
  padding-top: 5px;
  cursor: pointer;
`;

const Flexbox = styled.div<IThemeProps>`
  display: flex;
  position: absolute;
  z-index: ${(props) => props.theme.zIndex.h200};
  padding: 0;
  width: 100%;
  bottom: 0;
  top: 0;
  ${(props) =>
    props.theme.borders.interaction && `border: 4px solid transparent;`}

  &.fadeIn {
    animation: ${moveDownAndFadeIn({ size: animationHeight })} 250ms linear;
  }

  &.fadeOut {
    opacity: 0;
    transform: translateY(-${animationHeight});
    animation: ${moveUpAndFadeOut({ size: animationHeight })} 150ms linear;
  }
`;

const Content = styled.div<IThemeProps>`
  display: flex;
  position: relative;
  padding: 20px 10px 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: ${(props) => props.theme.colors.notification};
  ${(props) =>
    props.theme.borders.interaction &&
    `border-radius: ${props.theme.borders.interaction};`}
  justify-content: center;
  @media ${(props) => props.theme.breakpoints.mobile} {
    justify-content: left;
  }
  align-items: center;
  color: ${(props) => props.theme.colors.fontTextLight};
`;

export default Overlay;
